import { router } from '@inertiajs/react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha, styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import ColorModeIconDropdown from '../../../theme/ColorModeIconDropdown';
import { FONT_BODY, FONT_DISPLAY, TEAL } from './LandingKit';
import Sitemark from './SitemarkIcon';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: 16,
  padding: '8px 8px 8px 18px',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(2,20,18,0.08)',
  backgroundColor: 'rgba(255,255,255,0.72)',
  boxShadow: '0 10px 34px rgba(2,20,18,0.10)',
  ...theme.applyStyles('dark', {
    border: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(8,12,14,0.62)',
    boxShadow: '0 10px 34px rgba(0,0,0,0.5)',
  }),
}));

export default function AppAppBar({ showNavigation = true }) {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const handleNavigation = (path) => {
    if (path.startsWith('/#')) {
      // For anchor links, use router.visit to navigate to landing page with hash
      router.visit(path);
    } else {
      // For regular routes, use router.visit
      router.visit(path);
    }
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2.5 }}>
              <Sitemark />
              <Typography
                component="span"
                sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', lineHeight: 1, color: 'text.primary' }}
              >
                Drive<Box component="span" sx={{ color: TEAL }}>Sync</Box>
              </Typography>
            </Box>
            {showNavigation && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.25, ml: 1 }}>
                {[
                  ['Funcionalidades', '/#features'],
                  ['Como funciona', '/#how'],
                  ['Destaques', '/#highlights'],
                  ['Preços', '/#pricing'],
                  ['Perguntas Frequentes', '/#faq'],
                ].map(([label, href]) => (
                  <Button
                    key={href}
                    variant="text"
                    size="small"
                    onClick={() => handleNavigation(href)}
                    sx={{
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: 'text.secondary',
                      px: 1.25,
                      borderRadius: 2,
                      transition: 'color 0.15s ease, background-color 0.15s ease',
                      '&:hover': { color: TEAL, backgroundColor: alpha(TEAL, 0.1) },
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => handleNavigation('/users/sign_in')}
              sx={{
                fontFamily: FONT_BODY,
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'none',
                color: 'text.primary',
                px: 1.5,
                borderRadius: 999,
                '&:hover': { color: TEAL, backgroundColor: alpha(TEAL, 0.1) },
              }}
            >
              Entrar
            </Button>
            <Button
              variant="text"
              size="small"
              disableElevation
              onClick={() => handleNavigation('/leads/new')}
              sx={{
                textDecoration: 'none',
                fontFamily: FONT_BODY,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.25,
                py: 0.6,
                color: '#04110f',
                backgroundImage: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                '&:hover': { backgroundImage: `linear-gradient(180deg, ${TEAL}, #00a594)`, boxShadow: `0 8px 22px ${TEAL}66` },
              }}
            >
              Agendar demonstração
            </Button>
            <ColorModeIconDropdown />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                {showNavigation && (
                  <>
                    <MenuItem onClick={() => handleNavigation('/#features')}>Funcionalidades</MenuItem>
                    <MenuItem onClick={() => handleNavigation('/#how')}>Como funciona</MenuItem>
                    <MenuItem onClick={() => handleNavigation('/#highlights')}>Destaques</MenuItem>
                    <MenuItem onClick={() => handleNavigation('/#pricing')}>Preços</MenuItem>
                    <MenuItem onClick={() => handleNavigation('/#faq')}>Perguntas Frequentes</MenuItem>
                  </>
                )}
                <Divider sx={{ my: 3 }} />
                <MenuItem>
                  <Button
                    color="primary"
                    variant="contained"
                    fullWidth
                    onClick={() => handleNavigation('/leads/new')}
                    sx={{ textDecoration: 'none' }}
                  >
                    Agendar demonstração
                  </Button>
                </MenuItem>
                <MenuItem>
                  <Button
                    color="primary"
                    variant="outlined"
                    fullWidth
                    onClick={() => handleNavigation('/users/sign_in')}
                    sx={{ textDecoration: 'none' }}
                  >
                    Entrar
                  </Button>
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
